import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { f as usePrivateMutation, a as privateQuery } from '../../requestsCollection-hvMBC1_v.js';
import { ExchangeAccorAuthCodeForAccessToken, LogoutAccor, CustomerAccorData } from '../../services/graphql/accor/index.js';
import { CoreContext } from '../index.js';
import logger from '../../utils/logger.js';
import '../../animations--PMkOS0j.js';
import '../../index-QrdBfapu.js';
import 'cookies-next';
import '../../currency-_kacFJHO.js';
import '../../language-BHHwgtQp.js';
import '../../payment-5CMab_Gs.js';
import '../../index-vPolXUOt.js';
import useShowError from '../../hooks/useShowError.js';
import 'notistack';
import { useTranslation } from 'next-i18next';
import 'lodash/get';
import '../../product-_pRduMz7.js';
import '../../helpers-qyZpLI0y.js';
import 'lodash';
import '../../utils/currency.js';
import '../../utils/price.js';
import 'next/server';
import '../../index-Oip0gjfj.js';
import '../../graphqlRequestClient-7kdUKc_c.js';
import 'graphql-request';
import 'lodash/cloneDeep';
import '../../services/auth/index.js';
import '../../utils/logToSlack.js';
import '../../utils/cookies.js';
import '../../cookies-l-jEMp6P.js';
import '../../utils/isBot.js';
import '../../utils/fetchPolicyLookup.js';
import '../../_commonjsHelpers-jjO7Zipk.js';
import '../../index-RkBJ48Nr.js';
import 'i18next';
import 'react-i18next';
import '../../index-7BRgQqgo.js';
import '../../utils/error.js';
import '../../serverErrors-XxD2s73I.js';
import '../../utils/logToRelic.js';
import '../../utils/product.js';
import '../../paths-ZKN3m62_.js';
import 'number-currency-format';

/* ----------------------------------------------------------------
   helpers & constants
---------------------------------------------------------------- */
const ACCOR_USER_KEY = 'accorUser';
/**
 * Get the Accor authentication domain from environment variables
 * @returns The Accor domain to use for authentication
 */
const getAccorAuthDomain = () => {
    const domain = process.env.NEXT_PUBLIC_ACCOR_AUTH_DOMAIN;
    if (!domain) {
        logger('NEXT_PUBLIC_ACCOR_AUTH_DOMAIN environment variable is required', {
            type: 'error',
            category: 'api',
            // Avoid noisy console errors in production while still allowing external logging if enabled
            skipConsole: process.env.NODE_ENV === 'production',
        });
        // Fallback to rec-login for development safety
        return 'rec-login.accor.com';
    }
    return domain;
};
const EMPTY_USER = {
    points: 0,
    firstname: '',
    lastname: '',
    card_number: '',
    email: '',
    status_level: '',
};
const readAccorUser = () => {
    try {
        const raw = localStorage.getItem(ACCOR_USER_KEY);
        return raw ? JSON.parse(raw) : null;
    }
    catch (_a) {
        return null;
    }
};
const writeAccorUser = (user) => {
    if (user)
        localStorage.setItem(ACCOR_USER_KEY, JSON.stringify(user));
    else
        localStorage.removeItem(ACCOR_USER_KEY);
};
/* ----------------------------------------------------------------
   auth helpers (login / signup / logout)
---------------------------------------------------------------- */
const loginWithAccor = () => {
    const callbackUrl = `${window.location.origin}/accor/callback`;
    const accorClientId = process.env.NEXT_PUBLIC_ACCOR_AFFILIATE_PROGRAM_CLIENT_ID;
    const accorDomain = getAccorAuthDomain();
    const loginUrl = `https://${accorDomain}/as/authorization.oauth2?response_type=code&client_id=${accorClientId}&redirect_uri=${callbackUrl}&scope=openid LOYALTY.ROLE_PARTICIPANT`;
    localStorage.setItem('postLoginRedirect', window.location.pathname);
    window.location.href = loginUrl;
};
const signUpWithAccor = () => {
    const callbackUrl = `${window.location.origin}/accor/callback`;
    const accorClientId = process.env.NEXT_PUBLIC_ACCOR_AFFILIATE_PROGRAM_CLIENT_ID;
    const accorDomain = getAccorAuthDomain();
    const signUpUrl = `https://${accorDomain}/as/authorization.oauth2?response_type=code&client_id=${accorClientId}&accorregister=true&redirect_uri=${callbackUrl}&scope=openid LOYALTY.ROLE_PARTICIPANT`;
    localStorage.setItem('postLoginRedirect', window.location.pathname);
    window.location.href = signUpUrl;
};
/* ----------------------------------------------------------------
   data fetcher
---------------------------------------------------------------- */
const fetchAccorUser = async (apolloConfig) => {
    const { data, errors } = await privateQuery(apolloConfig(), { query: CustomerAccorData }, 'CustomerAccorData', undefined, true);
    if (errors.length)
        console.error('Error fetching customer Accor data:', errors);
    return { data, errors };
};
/* ----------------------------------------------------------------
   React context
---------------------------------------------------------------- */
const AccorAuthContext = createContext(null);
const AccorAuthProvider = ({ children }) => {
    const { apolloConfig } = useContext(CoreContext);
    const router = useRouter();
    const showError = useShowError();
    const { t } = useTranslation('', { keyPrefix: '' });
    /* -------- hydration‑safe initial state -------- */
    const [accorUser, setAccorUser] = useState(EMPTY_USER);
    const [isAccorLoggedIn, setIsAccorLoggedIn] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    /* -------- graphql mutation -------- */
    const [exchangeCode] = usePrivateMutation(apolloConfig(), ExchangeAccorAuthCodeForAccessToken, undefined, true);
    const [logoutAccor] = usePrivateMutation(apolloConfig(), LogoutAccor, undefined, true);
    const logoutWithAccor = async () => {
        await logoutAccor();
        writeAccorUser(null);
        setAccorUser(EMPTY_USER);
        setIsAccorLoggedIn(false);
    };
    /* -------- validate session on page load -------- */
    const validateUserSession = async () => {
        if (isValidating)
            return;
        setIsValidating(true);
        try {
            const { errors } = await fetchAccorUser(apolloConfig);
            // Check for the specific session error or any other errors
            if (errors.length > 0 &&
                errors[0].message.includes('User session does not have a valid associated session')) {
                // Session is invalid - log out user and show generic error
                await logoutWithAccor();
                showError(t('$common.$validation.accorTryLogInAgain'));
                return;
            }
            if (errors.length > 0) {
                console.error('Error validating user session:', errors[0].message);
                // Other errors - log out user and show accor error
                await logoutWithAccor();
                showError(errors[0].message);
            }
        }
        catch (err) {
            console.error('Error validating user session:', err);
            // Session validation failed - log out user and show generic error
            await logoutWithAccor();
            showError(err || t('$common.$validation.accorTryLogInAgain'));
        }
        finally {
            setIsValidating(false);
        }
    };
    /* -------- after mount: read localStorage -------- */
    useEffect(() => {
        const stored = readAccorUser();
        if (stored) {
            setAccorUser(stored);
            setIsAccorLoggedIn(true);
            // Validate session with the API to ensure token is still valid
            validateUserSession();
        }
    }, []);
    /* -------- persist changes -------- */
    useEffect(() => {
        writeAccorUser(isAccorLoggedIn ? accorUser : null);
    }, [isAccorLoggedIn, accorUser]);
    /* -------- cross‑tab sync -------- */
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key !== ACCOR_USER_KEY)
                return;
            const newUser = e.newValue ? JSON.parse(e.newValue) : null;
            setAccorUser(newUser !== null && newUser !== void 0 ? newUser : EMPTY_USER);
            setIsAccorLoggedIn(!!newUser);
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    /* -------- handle redirect callback -------- */
    useEffect(() => {
        let isMounted = true;
        const code = new URLSearchParams(window.location.search).get('code');
        const accorError = new URLSearchParams(window.location.search).get('error_description');
        const postLoginRedirect = localStorage.getItem('postLoginRedirect');
        const handlePostLoginRedirect = (errorMessage) => {
            if (postLoginRedirect) {
                localStorage.removeItem('postLoginRedirect');
                router.push(postLoginRedirect);
                if (errorMessage) {
                    setTimeout(() => {
                        showError(errorMessage);
                    }, 2000);
                }
            }
        };
        const handleLoginFailed = () => {
            console.error('Failed to exchange code for access token');
            setAccorUser(EMPTY_USER);
            setIsAccorLoggedIn(false);
            handlePostLoginRedirect(t('$common.$validation.accorServiceUnavailable'));
        };
        const handleCallback = async () => {
            var _a;
            try {
                const callbackUrl = `${window.location.origin}/accor/callback`;
                const { data } = await exchangeCode({
                    variables: { authCode: code, redirectUri: callbackUrl },
                });
                if (((_a = data === null || data === void 0 ? void 0 : data.exchangeAccorAuthCodeForAccessToken) === null || _a === void 0 ? void 0 : _a.success) && isMounted) {
                    const { data: customerData } = await fetchAccorUser(apolloConfig);
                    if (customerData) {
                        setAccorUser(customerData.customerAccorData);
                        setIsAccorLoggedIn(true);
                        handlePostLoginRedirect();
                    }
                    else {
                        handleLoginFailed();
                    }
                }
                else {
                    handleLoginFailed();
                }
            }
            catch (err) {
                console.error('Error during Accor login:', err);
                handleLoginFailed();
            }
        };
        if (code)
            handleCallback();
        if (accorError)
            handleLoginFailed();
        return () => {
            isMounted = false;
        };
    }, []);
    /** -------------------------------------------------
     *  Refresh the user from the API and propagate state
     * ------------------------------------------------*/
    const refreshAccorUser = async () => {
        try {
            const { data, errors } = await fetchAccorUser(apolloConfig);
            if (errors.length)
                console.error(errors[0].message);
            if (data === null || data === void 0 ? void 0 : data.customerAccorData) {
                setAccorUser(data.customerAccorData);
                setIsAccorLoggedIn(true);
            }
        }
        catch (err) {
            console.error('Failed to refresh Accor user', err);
        }
    };
    /* -------- memoised value -------- */
    const contextValue = React.useMemo(() => ({
        loginWithAccor,
        logoutWithAccor,
        isAccorLoggedIn,
        accorUser,
        setAccorUser,
        refreshAccorUser,
    }), [isAccorLoggedIn, accorUser]);
    return React.createElement(AccorAuthContext.Provider, { value: contextValue }, children);
};

export { AccorAuthContext, AccorAuthProvider, loginWithAccor, signUpWithAccor };
//# sourceMappingURL=index.js.map
